import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, User, Calendar, Megaphone, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PortalLayout, SectionTitle, Panel, EmptyState } from "@/components/platform/PortalLayout";
import MemberBilling from "@/components/platform/MemberBilling";

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function ParentDashboard() {
  const [athletes, setAthletes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "", age: "", school: "" });
  const [saving, setSaving] = useState(false);

  const loadBase = useCallback(async () => {
    try {
      const [{ data: aths }, { data: sess }, { data: anns }] = await Promise.all([
        api.get("/athletes"), api.get("/sessions"), api.get("/announcements"),
      ]);
      setAthletes(aths);
      setSessions(sess);
      setAnnouncements(anns);
      setSelectedId((prev) => prev || (aths[0]?.id ?? null));
    } catch (e) {
      toast.error("Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  useEffect(() => {
    if (!selectedId) { setAttendance([]); setEvals([]); return; }
    (async () => {
      try {
        const [{ data: att }, { data: ev }] = await Promise.all([
          api.get(`/attendance?athlete_id=${selectedId}`),
          api.get(`/evaluations?athlete_id=${selectedId}`),
        ]);
        setAttendance(att); setEvals(ev);
      } catch (e) { toast.error("Failed to load athlete details."); }
    })();
  }, [selectedId]);

  const addAthlete = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/athletes", form);
      toast.success("Athlete added.");
      setForm({ name: "", sport: "", age: "", school: "" });
      setShowAdd(false);
      setAthletes((p) => [data, ...p]);
      setSelectedId(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const selected = athletes.find((a) => a.id === selectedId);
  const athleteSessions = sessions.filter((s) => (s.athlete_ids || []).includes(selectedId));

  if (loading) {
    return <PortalLayout roleLabel="Parent Portal"><div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div></PortalLayout>;
  }

  return (
    <PortalLayout roleLabel="Parent Portal">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">My Athletes</h1>
        <button data-testid="parent-add-athlete" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "Add Athlete"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addAthlete} className="bg-[#121212] border border-white/10 p-6 mb-8 grid sm:grid-cols-4 gap-4" data-testid="parent-add-form">
          <input data-testid="add-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <input data-testid="add-sport" placeholder="Sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className={inputCls} />
          <input data-testid="add-age" placeholder="Age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} />
          <input data-testid="add-school" placeholder="School" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className={inputCls} />
          <button data-testid="add-submit" disabled={saving} className="sm:col-span-4 justify-self-start inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save Athlete
          </button>
        </form>
      )}

      {athletes.length === 0 ? (
        <EmptyState testid="parent-no-athletes">No athletes yet. Add your athlete to get started.</EmptyState>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap mb-8" data-testid="parent-athlete-switcher">
            {athletes.map((a) => (
              <button key={a.id} data-testid={`parent-select-${a.id}`} onClick={() => setSelectedId(a.id)}
                className={`px-4 py-2.5 text-sm uppercase tracking-widest font-bold border transition-colors ${a.id === selectedId ? "bg-white border-white text-black" : "border-white/20 text-white/60 hover:text-white"}`}>
                {a.name}
              </button>
            ))}
          </div>

          {selected && (
            <div className="grid lg:grid-cols-3 gap-6" data-testid="parent-athlete-detail">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <SectionTitle>Profile</SectionTitle>
                  <Panel>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center"><User size={22} /></div>
                      <div><p className="font-display font-bold uppercase text-2xl tracking-tight">{selected.name}</p>
                        <p className="text-white/50 text-sm">{[selected.sport, selected.position].filter(Boolean).join(" · ") || "—"}</p></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      {[["Age", selected.age], ["Grad Year", selected.grad_year], ["School", selected.school], ["Status", selected.status]].map(([k, v]) => (
                        <div key={k}><p className="text-white/40 text-xs uppercase tracking-wide">{k}</p><p className="mt-1">{v || "—"}</p></div>
                      ))}
                    </div>
                  </Panel>
                </div>
                <div>
                  <SectionTitle>Evaluations</SectionTitle>
                  {evals.length ? evals.map((ev) => (
                    <Panel key={ev.id} className="mb-3"><p className="font-bold">{ev.title}</p>{ev.summary && <p className="text-white/60 text-sm mt-1">{ev.summary}</p>}</Panel>
                  )) : <EmptyState testid="parent-no-evals">No evaluations yet.</EmptyState>}
                </div>
                <div>
                  <SectionTitle>Attendance</SectionTitle>
                  {attendance.length ? (
                    <Panel data-testid="parent-attendance"><div className="space-y-2">{attendance.map((r) => (
                      <div key={r.id} className="flex justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                        <span className="text-white/60">{new Date(r.date).toLocaleDateString()}</span>
                        <span className="uppercase tracking-widest text-xs font-bold">{r.status}</span>
                      </div>))}</div></Panel>
                  ) : <EmptyState testid="parent-no-attendance">No attendance records yet.</EmptyState>}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <SectionTitle>Schedule</SectionTitle>
                  {athleteSessions.length ? athleteSessions.map((s) => (
                    <Panel key={s.id} className="mb-3"><div className="flex items-center gap-2 mb-1"><Calendar size={15} className="text-white/60" /><p className="font-bold">{s.title}</p></div>
                      <p className="text-white/50 text-sm">{new Date(s.start_time).toLocaleString()}</p>{s.location && <p className="text-white/40 text-sm">{s.location}</p>}</Panel>
                  )) : <EmptyState testid="parent-no-sessions">No sessions scheduled.</EmptyState>}
                </div>
                <div>
                  <SectionTitle>Billing & Membership</SectionTitle>
                  <MemberBilling />
                </div>
                <div>
                  <SectionTitle>Announcements</SectionTitle>
                  {announcements.length ? announcements.map((a) => (
                    <Panel key={a.id} className="mb-3"><div className="flex items-center gap-2 mb-1"><Megaphone size={14} className="text-white/50" /><p className="font-bold text-sm">{a.title}</p></div><p className="text-white/60 text-sm">{a.body}</p></Panel>
                  )) : <EmptyState testid="parent-no-announcements">No announcements.</EmptyState>}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </PortalLayout>
  );
}
